import { Component, ContentChild, ElementRef, EventEmitter, Input, OnInit, Optional, Output, Self, TemplateRef, ViewChild, input } from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { IonModal } from '@ionic/angular';
import { InfiniteScrollCustomEvent, IonSearchbar } from '@ionic/angular/standalone';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { IonicComponentsModule } from 'src/app/modules/ionic-components.module';
import { UtilService } from 'src/app/services/util.service';
import InfiniteScrollSubject from 'src/app/subjects/infiniteScrollSubject';

@Component({
  selector: 'fv-multiselect-with-store',
  templateUrl: './multiselect-with-store.component.html',
  styleUrls: ['./multiselect-with-store.component.scss'],
  standalone: true,
  imports: [
    IonicComponentsModule
  ]
})
export class MultiselectWithStoreComponent  implements ControlValueAccessor {

  //#region Propiedades

  @Input("label") public label: string
  @Input("value-field") public valueField: string = 'Id'
  @Input("display-field") public displayField: string = 'Text'
  @Input("placeholder") public placeholder: string = "[Selecciona]"
  @Input("modal-title") public modalTitle: string = "Elije una opción"
  @Input("accept-text") public acceptText: string = "Aceptar"
  @Input("cancel-text") public cancelText: string = "Cancelar"
  @ViewChild("modal") public modal: IonModal
  @ContentChild("itemTpl") public comboItemTpl: TemplateRef<any>
  @ContentChild("displayTpl") public displayTpl: TemplateRef<any>
  @ContentChild("selectorTpl") public selectorTpl: TemplateRef<any>
  @Input("data-source") public dataSource: InfiniteScrollSubject<any>
  @Input("page-size") public pageSize: number = 5
  @Input("show-search") public showSearch: boolean = false
  @Input("search-placeholder") public searchPlaceholder: string = 'Buscar...'
  @Input("empty-text") public emptyText: string = "No se han encontrado elementos"
  @ViewChild("searchBar") public searchBar: IonSearchbar
  @ViewChild("refresher", { read: ElementRef<HTMLElement> }) public refresher: ElementRef<HTMLElement>
  @Input("readonly") public readonly: boolean
  @Input("obligatorio") public obligatorio: boolean // Esto es solo para pintar un asterisco, y este campo no se lee de los validadores, sino que hay que establecerlo a mano.

  public text = new BehaviorSubject<string>('')
  public items = new BehaviorSubject<any[]>(null)
  private _value: any[]
  public get value(): any[] {
    return this._value
  }
  @Input("value") public set value(val: any[]) {
    if (this._value !== val) {
      this._value = val
      let text = this.text
      let items = this.items
      let itemTexts: string[] = []
      if (this._value != null) {
        (async () => {
          for (let itemValue of this._value) {
            let itemById = await this.dataSource.getById(itemValue)
            if (itemById) {
              let itemText = itemById[this.displayField]
              if (itemText) {
                itemTexts.push(itemText)
              }
            }
          }
          text.next(itemTexts.join(', '))
          items.next(this._value)
        })()
      } else {
        this.text.next(null)
        this.items.next(null)
      }
    }
  }
  @Output("valueChange") public valueChange = new EventEmitter<any[]>()
  @Output("valueSelected") public valueSelected = new EventEmitter<{newValue: string, oldValue: string}>()

  //#endregion

  //#region Constructor

  constructor(
    private util: UtilService,
    @Self() @Optional() private control: NgControl
  ) {
    this.control.valueAccessor = this
  }

  //#endregion

  //#region Loader

  public showLoader(): void {
    this.refresher.nativeElement.classList.add('refresher-active')
    this.refresher.nativeElement.classList.add('refresher-refreshing')
  }

  public hideLoader(): void {
    this.refresher.nativeElement.classList.remove('refresher-active')
    this.refresher.nativeElement.classList.remove('refresher-refreshing')
  }

  //#endregion

  //#region Eventos

  public async contentClick(): Promise<void> {
    if (!this.readonly) {
      this.modal.isOpen = true
      await this.util.Sleep(0)
      if (this.searchBar != null) {
        this.searchBar.value = ''
      }
      if (this.dataSource != null) {
        this.dataSource.search = ''
        await this.dataSource.reset()
      }
    }
  }

  public optionSelected(option: any) {
    let oldValue = this.value.map(p => p.toString()).join(';')
    if (option == null) return
    let optionValue = option[this.valueField]
    this.value = this.value || []
    if (this.value.indexOf(optionValue?.toString()) == -1) {
      this.value.push(optionValue.toString())
    } else {
      this.value = this.value.filter(p => p != optionValue)
    }
    this.value = this.value.filter(p => p != '')

    this.valueChange.emit(this.value)
    this.valueSelected.emit({newValue: this.value.map(p => p.toString()).join(';'), oldValue: oldValue})
    this.onChange(this.value.map(p => p.toString()).join(';'))
    this.onTouched()
  }

  public clearValue() {
    let oldValue = this.value.map(p => p.toString()).join(';')
    this.value = []
    this.valueChange.emit(this.value)
    this.valueSelected.emit({newValue: '', oldValue: oldValue})
    this.onChange(null)
    this.modal.isOpen = false
    this.onTouched()
  }

  public async onSearch(): Promise<void> {
    if (this.dataSource != null) {
      try {
        this.dataSource.search = this.searchBar.value
        this.showLoader()
        await this.dataSource.reset()
      } catch (ex) {}
      this.hideLoader()
    }
  }

  public async onIonInfinite(ev: InfiniteScrollCustomEvent): Promise<void> {
    if (await firstValueFrom(this.dataSource.HasMoreItems)) {
      await this.dataSource.loadPage()
    }
    ev.target.complete()
  }

  public async handleRefresh(ev: Event): Promise<void> {
    if (this.dataSource != null) {
      await this.dataSource.reset()
      let target = ev.target as HTMLIonRefresherElement
      target.complete()
    }
  }

  //#endregion

  //#region Reactive Forms

  private onChange = (value: string) => { }
  private onTouched = () => { }

  writeValue(value: string): void {
    this.value = value.split(";")
    this.valueChange.emit(this._value)
    this.onChange(value)
  }

  registerOnChange(fn: (value: string) => {}): void {
    this.onChange = fn
  }

  registerOnTouched(fn: () => {}): void {
    this.onTouched = fn
  }

  public isInvalid(): boolean {
    return this.control ? this.control.invalid : false
  }

  public isTouched(): boolean {
    return this.control ? this.control.touched : false
  }

  //#endregion

}
